import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';

/**
 * Google Sheet mirror controls. The sheet keeps itself current from the
 * dashboard's own writes — this page is for the first backfill and for
 * repairing drift (a sheet write that failed, or a CSV import, which is too
 * large to mirror row by row).
 */
export function SheetPage() {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const status = useQuery({
    queryKey: ['sheet-status'],
    queryFn: () => api.sheetStatus(),
  });

  const backfill = useMutation({
    // Walk the chunks: a full rebuild is one delete per row against a
    // third-party API, far too slow to finish inside a single request.
    mutationFn: async () => {
      let offset: number | null = 0;
      let written = 0;
      let verified: boolean | null = null;
      let sheetRows: number | null = null;
      setProgress({ done: 0, total: 0 });
      while (offset !== null) {
        const r = await api.sheetBackfill(offset);
        written += r.rows;
        offset = r.next_offset;
        verified = r.verified;
        sheetRows = r.sheet_rows;
        setProgress({ done: written, total: r.total });
      }
      return { written, verified, sheetRows };
    },
    onMutate: () => {
      setResult(null);
      setError(null);
    },
    onSuccess: (r) => {
      const wrote = `Wrote ${r.written} row(s) to the sheet.`;
      // verified === false means the sheet service accepted writes it didn't
      // actually apply, which it does silently. Say so rather than claim success.
      setResult(
        r.verified === false
          ? `${wrote} Warning: the sheet reports ${r.sheetRows} row(s), not ${r.written} — ` +
              'some writes were dropped. Run Rebuild again.'
          : wrote,
      );
      setProgress(null);
      queryClient.invalidateQueries({ queryKey: ['sheet-status'] });
    },
    onError: (err) => {
      setProgress(null);
      setError(
        err instanceof ApiError
          ? `HTTP ${err.status} — ${
              typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail)
            }`
          : err instanceof Error
            ? err.message
            : String(err),
      );
    },
  });

  const s = status.data;
  const sheetUrl = s?.sheet_id
    ? `https://docs.google.com/spreadsheets/d/${s.sheet_id}/edit`
    : null;
  const drifted = s?.sheet_rows != null && s.sheet_rows !== s.db_rows;

  return (
    <div className="card">
      <p style={{ marginTop: 0 }}>
        Published, hidden and sold listings are mirrored into a Google Sheet as they change in this
        dashboard. Drafts stay out until they are published. Edits are applied by deleting the row
        and re-appending it, so an edited listing moves to the bottom of the sheet — sort by
        Inventory Id if you need a stable order.
      </p>

      {status.isLoading ? (
        <p>Checking sheet…</p>
      ) : status.isError ? (
        // Distinct from "not configured" on purpose: a failed status call means
        // we don't know the state, and saying "not configured" sends you off
        // checking secrets that are fine.
        <p style={{ color: 'var(--danger)' }}>
          Couldn&apos;t reach the sync status endpoint, so its state is unknown.{' '}
          {status.error instanceof ApiError
            ? `The API returned HTTP ${status.error.status}.`
            : String(status.error)}{' '}
          Check that the admin app is being used on its real hostname and that the worker is
          deployed.
        </p>
      ) : !s?.configured ? (
        <p style={{ color: 'var(--warn)' }}>
          The worker reports sync as not configured. Set <code>SHEETS_SHEET_ID</code> in{' '}
          <code>wrangler.toml</code> and the <code>SHEETS_API_KEY</code> secret, then redeploy.
        </p>
      ) : (
        <>
          <table style={{ marginBottom: '1rem' }}>
            <tbody>
              <tr>
                <th style={{ width: 200, textAlign: 'left' }}>Sheet</th>
                <td>
                  {sheetUrl ? (
                    <a href={sheetUrl} target="_blank" rel="noreferrer">
                      {s.tab}
                    </a>
                  ) : (
                    s.tab
                  )}
                </td>
              </tr>
              <tr>
                <th style={{ textAlign: 'left' }}>Listings in dashboard</th>
                <td>{s.db_rows}</td>
              </tr>
              <tr>
                <th style={{ textAlign: 'left' }}>Rows in sheet</th>
                <td>
                  {s.sheet_rows ?? '—'}
                  {drifted ? (
                    <span style={{ color: 'var(--warn)' }}> — out of sync</span>
                  ) : s.sheet_rows != null ? (
                    <span style={{ color: 'var(--muted)' }}> — matching</span>
                  ) : null}
                </td>
              </tr>
            </tbody>
          </table>

          {s.error ? (
            <p style={{ color: 'var(--danger)' }}>Could not read the sheet: {s.error}</p>
          ) : null}

          <button className="primary" disabled={backfill.isPending} onClick={() => backfill.mutate()}>
            {backfill.isPending && progress
              ? `Working… ${progress.done}/${progress.total || '?'}`
              : 'Backfill sheet'}
          </button>

          {drifted ? (
            <p style={{ fontSize: '0.85rem', marginBottom: 0 }}>
              <strong>To resync:</strong> open the sheet, select row 2 through the last row and
              delete them (keep the header row), then click Backfill.
            </p>
          ) : null}

          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: 0 }}>
            Backfill only writes to an empty tab, and appends in bulk. It deliberately cannot
            rewrite rows in place: Google Sheets rate-limits writes to roughly 60 per minute, and
            the sheet service reports throttled writes as successful without applying them — so
            deleting and re-adding ~180 rows loses most of them. Clearing the tab by hand costs no
            API writes and is the reliable reset.
          </p>
        </>
      )}

      {result ? (
        <div className="card" style={{ marginTop: '1rem' }}>
          <strong>{result}</strong>
        </div>
      ) : null}

      {error ? (
        <div className="card" style={{ marginTop: '1rem', borderColor: 'var(--danger)' }}>
          <strong style={{ color: 'var(--danger)' }}>Sync failed</strong>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', marginTop: '0.5rem' }}>
            {error}
          </pre>
        </div>
      ) : null}
    </div>
  );
}
